<template>
  <Modal
    v-model:visible="isVisible"
    class="cancel-order-modal"
    :header="
      t('common.cancelParam', { param: t('orders.orders', numOrdersSelected) })
    "
    @keypress.enter="onConfirm"
    @keypress.esc="isVisible = false"
  >
    <p id="cancel-instructions">
      {{
        t('common.confirmCancelationParams', {
          param: t('orders.orders', numOrdersSelected),
          count: numOrdersSelected,
        })
      }}
      {{ t('orders.cancellationReasonSelection') }}
    </p>

    <Message
      v-if="hasShipments || numOrdersSelected > 1"
      severity="error"
      class="rygen-message"
    >
      <p>
        {{ t('orders.cancellingTheseOrdersWarning', numOrdersSelected) }}
      </p>
      <ul>
        <li>{{ t('removeOrders.invalidateRate') }}</li>
        <li>{{ t('removeOrders.changeLineItemStatus') }}</li>
        <!--
          TODO: Calculate which stops are at risk of being removed and show
          them in this warning, by name or city/state
        -->
        <li>{{ t('removeOrders.removeStop') }}</li>
      </ul>
    </Message>

    <form
      id="cancel-form"
      aria-labelledby="cancel-instructions"
      class="flex flex-column flex-gap-1"
    >
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        :input-id="`${uid}-reason`"
        :errors="v$.cancellationReason.id.$errors"
        :label="t('orders.cancellationReason')"
      >
        <RygenDropdown
          :id="inputId"
          v-model="cancellationReason"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :options="cancellationReasons.data"
          :loading="cancellationReasons.loading"
          option-label="name"
          append-to="body"
          filter
          :placeholder="t('orders.cancellationReason')"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${uid}-comments`"
        :label="t('common.comments', 2)"
        optional
      >
        <Textarea
          :id="inputId"
          v-model="cancellationComments"
          v-bind="ariaProps"
          :placeholder="t('common.comments', 2)"
        />
      </InputWrapper>
    </form>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="
          t('common.dontCancelParam', {
            param: t('orders.orders', numOrdersSelected),
          })
        "
        @click="isVisible = false"
      />

      <RygenButton
        :label="
          t('common.cancelParam', {
            param: t('orders.orders', numOrdersSelected),
          })
        "
        :severity="Severity.DANGER"
        icon="pi pi-times"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  required,
  useToast,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';
import { Loadable, Severity } from '@rygen/rygen-ui/model';

import api from '@/api';
import { CancellationReason, CancelOrderPayload } from '@/model';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Message from 'primevue/message';
import Textarea from 'primevue/textarea';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  numOrdersSelected: {
    type: Number,
    default: 1,
  },
  hasShipments: {
    type: Boolean,
    default: () => false,
  },
});
const { numOrdersSelected, hasShipments } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel-order', payload: CancelOrderPayload): void;
}>();

const { t } = useI18n();
const toast = useToast();
const { uid } = useUid('cancel-order-modal');

const cancellationReason = ref<CancellationReason>();
const validationObject = computed(() => ({ cancellationReason }));

const rules = {
  cancellationReason: {
    id: {
      required: required(t('orders.cancellationReason')),
    },
  },
};
const { v$, validate } = useValidations(validationObject, rules, uid);

const cancellationComments = ref('');
const cancellationReasons: Ref<Loadable<CancellationReason[]>> = ref({
  loading: false,
});

const loadCancellationReasons = async () => {
  try {
    if (cancellationReasons.value.data) {
      return;
    }

    cancellationReasons.value.loading = true;
    cancellationReasons.value.data =
      await api.orderCancellationReason.findAll();
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    cancellationReasons.value.error = e;
    console.error('Error loading cancellation reasons', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('orders.cancellationReason'),
      }),
      error: e,
    });
  } finally {
    cancellationReasons.value.loading = false;
  }
};

const onConfirm = async () => {
  if ((await validate()) && cancellationReason.value) {
    emit('cancel-order', {
      cancellationReason: cancellationReason.value,
      cancellationComments: cancellationComments.value,
    });
    isVisible.value = false;
  }
};

const resetState = () => {
  cancellationReason.value = undefined;
  cancellationComments.value = '';
  v$.value.$reset();
};

watch(
  isVisible,
  (newVal: boolean) => {
    if (newVal) {
      resetState();
      loadCancellationReasons();
    }
  },
  { immediate: true },
);
</script>

<style>
.cancel-order-modal {
  p {
    margin-bottom: var(--sp-3);
  }

  .p-message {
    margin-bottom: var(--sp-6);
  }
}
</style>
